import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

// Inline Google "G" logo — sized to inherit currentColor's box, keeps the
// button legible on the dark theme without pulling in an SVG icon package.
const GoogleGlyph = () => (
    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
);

export default function Auth() {
    const [params] = useSearchParams();
    const isRegister = params.get("mode") === "register";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [googleBusy, setGoogleBusy] = useState(false);
    const [error, setError] = useState("");
    const { login, register, startGoogleLogin } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setError("");
        try {
            if (isRegister) await register(email, password);
            else await login(email, password);
            navigate("/terminal");
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const onGoogle = async () => {
        setGoogleBusy(true); setError("");
        try {
            await startGoogleLogin();
            // On success this navigates away — nothing more to do here.
        } catch (err) {
            setError(err.message);
            setGoogleBusy(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-3.5rem)] bg-[#050505] grid place-items-center px-4 relative">
            <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
            <div className="w-full max-w-md bg-[#0A0A0C] border border-[#1F1F24] p-6 sm:p-10 rounded-sm relative">
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-400 mb-3">
                    {isRegister ? "Register · Testnet" : "Sign in"}
                </div>
                <h1 className="text-2xl sm:text-3xl font-medium tracking-tighter">
                    {isRegister ? "Open testnet account" : "Welcome back, trader"}
                </h1>
                <p className="mt-3 text-sm text-zinc-400">
                    {isRegister
                        ? "A mock wallet address and 1,000,000 USDC will be auto-generated for you."
                        : "Use your registered email and password to enter the terminal."}
                </p>

                <form className="mt-8 grid gap-5" onSubmit={submit}>
                    <button
                        type="button"
                        data-testid="auth-google-button"
                        onClick={onGoogle}
                        disabled={googleBusy || busy}
                        className="flex items-center justify-center gap-2 bg-white hover:bg-zinc-100 disabled:opacity-50 text-zinc-900 font-semibold py-2.5 rounded-sm text-sm tracking-tight transition-colors"
                    >
                        <GoogleGlyph />
                        {googleBusy ? "Redirecting to Google…" : (isRegister ? "Sign up with Google" : "Continue with Google")}
                    </button>
                    <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-600">
                        <div className="flex-1 h-px bg-[#1F1F24]" />
                        or use email
                        <div className="flex-1 h-px bg-[#1F1F24]" />
                    </div>
                    <div className="grid gap-2">
                        <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Email</label>
                        <input
                            data-testid="auth-email-input"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm"
                        />
                    </div>
                    <div className="grid gap-2">
                        <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Password</label>
                        <input
                            data-testid="auth-password-input"
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm"
                        />
                    </div>
                    {error && (
                        <div data-testid="auth-error" className="text-xs text-rose-400 font-mono border border-rose-500/20 bg-rose-500/5 px-3 py-2 rounded-sm">
                            {error}
                        </div>
                    )}
                    <button
                        data-testid="auth-submit-button"
                        disabled={busy}
                        className="mt-2 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-50 text-black font-semibold py-3 rounded-sm text-sm tracking-tight transition-colors"
                    >
                        {busy ? "…" : isRegister ? "Create account →" : "Sign in →"}
                    </button>
                </form>

                <div className="mt-8 text-xs text-zinc-500">
                    {isRegister ? (
                        <>Already registered?{" "}
                            <Link to="/auth" className="text-cyan-400 hover:text-cyan-300">Sign in</Link>
                        </>
                    ) : (
                        <>New here?{" "}
                            <Link to="/auth?mode=register" className="text-cyan-400 hover:text-cyan-300">Create account</Link>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
